import React, { useCallback, useMemo, useEffect } from 'react';
import useMedia from 'hooks/useMedia';
import { PromiseState } from 'react-refetch';
import delve from 'dlv';
import styled from 'styled-components/macro';

import stringToSlug from 'helpers/stringToSlug';
import formatDate from 'helpers/formatDate';

import useDateRangeKPI from 'hooks/useDateRangeKPI';

import useExportUrl from '../Discovery/utils/useExportUrl';
import mapKPIData from '../mappings/mapKPIData';

import KeyValueGroup from 'components/base/KeyValueGroup/KeyValueGroup';
import KPIShortFormVideoMobileLoader from './KPIShortFormVideoMobileLoader';
import {
  Container,
  Header,
  KPIItem as KPIListItem,
  KPIListWrapper,
  KPIListItemColumn,
  KPIListItemContent,
  KPIListItemContentItem,
  KPIListItemContentItemColumn,
  KPIListItemRow,
  KPIListTotal as KPIList,
  DateRange,
} from '../shared';
import KPIWrapper from '../KPIWrapper/KPIWrapper';
import KPILoader from 'components/utility/ContentLoader/KPILoader/KPILoader';
import ErrorBoundary from 'components/utility/ErrorBoundary/ErrorBoundary';
import TopTerritories from '../shared/TerritoryBlock/TopTerritories';
import { TopCountry } from '../shared/TerritoryBlock/TopCountry';
import {
  ITopTerritoriesEntityCountryItem,
  ITopTerritoriesEntity,
} from '../shared/TerritoryBlock/types';
import { IShortFormVideoProps } from './types';
import interceptConnect from 'helpers/interceptConnect';
import useCountryShortFormVideoData from './utils/useCountryShortFormVideoData';
import { IToolbarItemType } from 'components/utility/GenericComponent/GenericComponent';

const VerticalLine = styled('div')<any>`
  width: 2px;
  height: ${({ lineHeight }): string => lineHeight && lineHeight};
  border: solid 1px #e8ebed;
  position: absolute;
  left: 50%;
  top: 330px;
`;

export const HeaderWrapper = styled('div')<any>`
  ${({ isMobileView }): string => (isMobileView ? 'margin-bottom: -2em;' : '')}
`;

const ListWrapper = styled('div')<any>`
  min-width: 100%;

  dd {
    span {
      line-height: 3rem;
    }
  }
`;

const KPISecondWrapper = styled('div')<any>`
  display: flex;
  flex-flow: row wrap;
  flex: 1 1 ${({ isMobileView }): string => (isMobileView ? '80%' : '50%')};
  margin-right: -10em;
  max-width: 100%;
  ${({ isMobileView }): string => (isMobileView ? 'margin-top: 20px;' : '')}

  @media screen and (max-width: 1150px) {
    margin-right: -8em;
  }
  @media screen and (max-width: 1050px) {
    margin-right: -6em;
  }
`;

const dataSources = (): any => {
  return {
    shortFormVideoFetch: (
      compareMode: string,
      countryIds: string[],
      entityIds: string[],
      entityType: string,
      filters: { country: string; region: string; platform: number },
      customRegion: any
    ): any => {
      return {
        shortFormVideoKPI: {
          url: 'shortFormVideokpi',
          interceptionData: {
            compareMode,
            countryIds,
            entityIds,
            entityType,
            filters,
            customRegion,
          },
          comparison: `${compareMode} : ${
            countryIds && countryIds.join()
          }: ${entityIds.join()} : ${entityType} ${filters?.country} ${
            filters?.region
          } ${filters?.platform}${customRegion}`,
        },
      };
    },
  };
};
const ShortFormVideoKPIs: React.FC<IShortFormVideoProps> = (props) => {
  const {
    appName,
    compareMode,
    countryIds,
    shortFormVideoKPI = PromiseState.create(),
    entityIds,
    entityType,
    filters,
    dataColors,
    shortFormVideoFetch,
    customRegionFilter,
  } = props;

  const {
    shortFormVideoKPIViewsData,
    shortFormVideoKPICreationsData,
    shortFormVideoCountriesViews,
    shortFormVideoCountriesCreations,
    reason,
    urlViews,
    urlCreations,
  } = (!shortFormVideoKPI.pending && shortFormVideoKPI.value) || {};

  const isMobileView = useMedia('(max-width: 50em)');
  const colorsForData = compareMode ? dataColors : ['#001247'];

  const exportUrlViews = useExportUrl(urlViews);
  const exportUrlCreations = useExportUrl(urlCreations);

  useEffect(() => {
    shortFormVideoFetch(
      compareMode,
      countryIds,
      entityIds,
      entityType,
      filters,
      customRegionFilter
    );
  }, [
    compareMode,
    countryIds,
    entityIds,
    entityType,
    filters,
    shortFormVideoFetch,
    customRegionFilter,
  ]);

  const [, lastWeek] = useDateRangeKPI('tiktok');

  const topCountriesTWTD = useCountryShortFormVideoData(
    shortFormVideoCountriesViews
  );
  const topCountriesLW = useCountryShortFormVideoData(
    shortFormVideoCountriesCreations
  );

  const compareArray = compareMode === 'country' ? countryIds : entityIds;
  const shortFormVideoViewsKPIs = useMemo(() => {
    if (shortFormVideoKPIViewsData && shortFormVideoKPIViewsData?.length > 0) {
      const data = [];
      if (compareMode) {
        shortFormVideoKPIViewsData.forEach((element: any) => {
          data.push({
            ...element,
            data: element.data[0],
          });
        });
      } else {
        data.push({
          ...shortFormVideoKPIViewsData[0],
          data: shortFormVideoKPIViewsData[0].data[0],
        });
      }
      return mapKPIData({
        data,
        compareMode,
        compareArray,
      });
    }
  }, [shortFormVideoKPIViewsData, compareMode, compareArray]);

  const shortFormVideoCreationsKPIs = useMemo(() => {
    if (
      shortFormVideoKPICreationsData &&
      shortFormVideoKPICreationsData.length > 0
    ) {
      const data = [];
      if (compareMode) {
        shortFormVideoKPICreationsData.forEach((element: any) => {
          data.push({
            ...element,
            data: element.data[0],
          });
        });
      } else {
        data.push({
          ...shortFormVideoKPICreationsData[0],
          data: shortFormVideoKPICreationsData[0].data[0],
        });
      }
      return mapKPIData({
        data,
        compareMode,
        compareArray,
      });
    }
  }, [shortFormVideoKPICreationsData, compareMode, compareArray]);

  /**
   * Returns a key for TopCountry list items
   */
  const getCountryKey = useCallback(
    (entity: ITopTerritoriesEntity, item: ITopTerritoriesEntityCountryItem) =>
      `top-country-${entity.entity.id}-${item.id}`,
    []
  );

  const KPIListItemBlock = compareMode ? KPIListItemColumn : KPIListItemRow;
  const lineHeight = topCountriesTWTD?.length
    ? `${5 + topCountriesTWTD[0]?.data?.length}%`
    : '5%';

  const getCreationsLastWeek = (): any => (
    <ListWrapper>
      <HeaderWrapper isMobileView={isMobileView}>
        <Header
          titleAlignLeft={!isMobileView}
          marginBottom={isMobileView && '1.2em'}
        >
          {isMobileView
            ? 'Last Full Week Creations'
            : 'Creations Last Full Week'}
          {lastWeek && (
            <DateRange>
              {`${formatDate(lastWeek?.startDate, 'day', true)} 
              - ${formatDate(lastWeek?.endDate, 'day', true)}`}
            </DateRange>
          )}
        </Header>
      </HeaderWrapper>
      {shortFormVideoKPI.pending &&
        (isMobileView ? (
          <KPIShortFormVideoMobileLoader />
        ) : (
          <KPILoader showPrevious showChange showRow />
        ))}
      {shortFormVideoCreationsKPIs && (
        <KeyValueGroup
          items={[
            {
              label: 'Creations',
              metric: delve(shortFormVideoCreationsKPIs, 'count_last_week'),
            },
            {
              label: 'Previous',
              metric: delve(
                shortFormVideoCreationsKPIs,
                'count_last_last_week'
              ),
            },
            {
              label: 'Change',
              metric: {
                ...delve(shortFormVideoCreationsKPIs, 'wow_last_completed'),
                type: 'newTrend',
              },
            },
          ]}
          compareMode={compareMode}
          dataColors={colorsForData}
          showRow
          isMobileView={isMobileView}
        />
      )}
    </ListWrapper>
  );

  return (
    <Container
      isMobileView={isMobileView}
      data-pw={stringToSlug('Short Form Video KPIs')}
    >
      <ErrorBoundary
        error={reason?.error ? new Error(reason?.error) : null}
        errorInfo={reason?.errorInfo || null}
      >
        <KPIWrapper
          allowedFilters={['country', 'region', 'customRegion', 'platform']}
          title={`${appName} KPIs`}
          glossarySlug="kpis/shortFormVideo-kpis"
          extraToolbarItems={[
            {
              label: 'Export Views Data',
              href: exportUrlViews,
              type: IToolbarItemType.Export,
              order: 1,
            },
            {
              label: 'Export Creations Data',
              href: exportUrlCreations,
              type: IToolbarItemType.Export,
              order: 2,
            },
          ]}
          titleAlignLeft
          isMobileView={isMobileView}
          filterMarginLeft="0px"
        >
          <KPIListWrapper isMobileView={isMobileView}>
            <KPIList>
              <KPIListItem>
                <KPIListItemContent>
                  <KPIListItemContentItem itemsCount={2}>
                    <KPIListItemRow>
                      <ListWrapper>
                        <HeaderWrapper isMobileView={isMobileView}>
                          <Header
                            titleAlignLeft={!isMobileView}
                            marginBottom={isMobileView && '1.2em'}
                          >
                            {isMobileView
                              ? 'Last Full Week Views'
                              : 'Views Last Full Week'}
                            {lastWeek && (
                              <DateRange>
                                {`${formatDate(
                                  lastWeek?.startDate,
                                  'day',
                                  true
                                )} 
                              - ${formatDate(lastWeek?.endDate, 'day', true)}`}
                              </DateRange>
                            )}
                          </Header>
                        </HeaderWrapper>
                        {shortFormVideoKPI.pending &&
                          (isMobileView ? (
                            <KPIShortFormVideoMobileLoader />
                          ) : (
                            <KPILoader showPrevious showChange showRow />
                          ))}
                        {shortFormVideoViewsKPIs && (
                          <KeyValueGroup
                            items={[
                              {
                                label: 'Views',
                                metric: delve(
                                  shortFormVideoViewsKPIs,
                                  'count_last_week'
                                ),
                              },
                              {
                                label: 'Previous',
                                metric: delve(
                                  shortFormVideoViewsKPIs,
                                  'count_last_last_week'
                                ),
                              },
                              {
                                label: 'Change',
                                metric: {
                                  ...delve(
                                    shortFormVideoViewsKPIs,
                                    'wow_last_completed'
                                  ),
                                  type: 'newTrend',
                                },
                              },
                            ]}
                            compareMode={compareMode}
                            dataColors={colorsForData}
                            showRow
                            isMobileView={isMobileView}
                          />
                        )}
                      </ListWrapper>
                    </KPIListItemRow>
                    <KPIListItemBlock>
                      <KPIListItemContentItemColumn>
                        <TopTerritories
                          label="Top Countries"
                          pending={shortFormVideoKPI.pending}
                          topTerritories={topCountriesTWTD}
                          TerritoryItem={TopCountry}
                          getKey={getCountryKey}
                        />
                      </KPIListItemContentItemColumn>
                    </KPIListItemBlock>
                  </KPIListItemContentItem>

                  {!isMobileView && !shortFormVideoKPI.pending && (
                    <VerticalLine lineHeight={lineHeight} />
                  )}

                  <KPISecondWrapper isMobileView={isMobileView}>
                    <KPIListItemContentItem itemsCount={2}>
                      {getCreationsLastWeek()}
                      <KPIListItemBlock>
                        <KPIListItemContentItemColumn
                          marginTop={isMobileView && '20px'}
                        >
                          <TopTerritories
                            label="Top Countries"
                            pending={shortFormVideoKPI.pending}
                            topTerritories={topCountriesLW}
                            TerritoryItem={TopCountry}
                            getKey={getCountryKey}
                            marginTop="-1.5em"
                          />
                        </KPIListItemContentItemColumn>
                      </KPIListItemBlock>
                    </KPIListItemContentItem>
                  </KPISecondWrapper>
                </KPIListItemContent>
              </KPIListItem>
            </KPIList>
          </KPIListWrapper>
        </KPIWrapper>
      </ErrorBoundary>
    </Container>
  );
};

export default interceptConnect(dataSources as any)(
  ShortFormVideoKPIs as any
) as any;
